import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

export const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_LOCALE = {
    CAD: "en-CA",
    USD: "en-US",
    EUR: "fr-FR",
    XOF: "fr-FR",
};

const CURRENCY_LABELS = {
    CAD: "CAD ($)",
    USD: "USD ($)",
    EUR: "EUR (€)",
    XOF: "FCFA",
};

export const SUPPORTED_CURRENCIES = ["CAD", "USD", "EUR", "XOF"];
export const getCurrencyLabel = (code) => CURRENCY_LABELS[code] || code;

// Tenant currency is stored globally after login
let _tenantCurrency = "EUR";
export const setTenantCurrency = (c) => { _tenantCurrency = c || "EUR"; };
export const getTenantCurrency = () => _tenantCurrency;

export const formatCurrency = (amount, currency) => {
    const curr = currency || _tenantCurrency || "EUR";
    const locale = CURRENCY_LOCALE[curr] || "fr-FR";
    if (curr === "XOF") {
        return new Intl.NumberFormat(locale, {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount) + " FCFA";
    }
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: curr,
    }).format(amount);
};

export const formatDate = (date) => {
    if (!date) return "-";
    return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(new Date(date));
};

export const formatDateTime = (date) => {
    if (!date) return "-";
    return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(new Date(date));
};

export const getStatusColor = (status) => {
    const colors = {
        // Client statuses
        prospect: "status-prospect",
        negotiation: "status-negotiation",
        active: "status-active",
        loyal: "status-loyal",
        // Project statuses
        planning: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        in_progress: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        on_hold: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
        completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
        cancelled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
        // Task statuses
        todo: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
        review: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
        // Invoice statuses
        draft: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
        sent: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
        partial: "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400",
        paid: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
        overdue: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
        // Quote statuses
        accepted: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
        rejected: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
        expired: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
    };
    return colors[status] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
};

export const getPriorityColor = (priority) => {
    const colors = {
        low: "priority-low",
        medium: "priority-medium",
        high: "priority-high",
        critical: "priority-critical",
    };
    return colors[priority] || "priority-medium";
};

export const getInitials = (name) => {
    if (!name) return "?";
    return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
};

export const statusLabels = {
    // Client
    prospect: "Prospect",
    negotiation: "En négociation",
    active: "Client actif",
    loyal: "Client fidèle",
    // Project
    planning: "Planification",
    in_progress: "En cours",
    on_hold: "En pause",
    completed: "Terminé",
    cancelled: "Annulé",
    // Task
    todo: "À faire",
    review: "En revue",
    // Invoice/Quote
    draft: "Brouillon",
    sent: "Envoyé",
    partial: "Partiel",
    paid: "Payé",
    overdue: "En retard",
    accepted: "Accepté",
    rejected: "Rejeté",
    expired: "Expiré",
};

export const priorityLabels = {
    low: "Basse",
    medium: "Moyenne",
    high: "Haute",
    critical: "Critique",
};
